import { Component, ErrorInfo, ReactNode } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  RefreshIcon,
  Text,
} from '@billda/design-system';
import { BugIcon } from '@billda/design-system';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log to console for debugging
    console.error('App Error Boundary caught an error:', error, errorInfo);

    // In production, you could send this to an error reporting service
    // Example: Sentry.captureException(error, { extra: errorInfo });
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="mx-auto w-full max-w-3xl px-4">
          <div className="flex min-h-screen flex-col items-center justify-center gap-6">
            <Card className="max-w-[500px] text-center">
              <CardContent className="p-8">
                <BugIcon
                  aria-hidden="true"
                  className="mx-auto mb-4 size-16 text-content-error"
                />

                <Text variant="h5" className="mb-2 text-content-error">
                  Something went wrong
                </Text>

                <Text variant="body1" color="secondary" className="mb-6">
                  We&apos;re sorry, but something unexpected happened. You can
                  try refreshing the page or contact support if the problem
                  persists.
                </Text>

                <Alert severity="error" className="mb-6 text-left">
                  <Text variant="body2">
                    {this.state.error?.message ||
                      'An unexpected error occurred'}
                  </Text>
                </Alert>

                <div className="flex justify-center gap-4">
                  <Button variant="neutral" onClick={this.resetError}>
                    <RefreshIcon aria-hidden="true" className="size-4" />
                    Try Again
                  </Button>
                  <Button variant="outline" onClick={this.handleReload}>
                    Reload Page
                  </Button>
                </div>

                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <div className="mt-6 text-left">
                    <Text variant="h6" className="mb-2 font-semibold">
                      Development Error Details:
                    </Text>
                    <pre className="max-h-[200px] overflow-auto rounded-lg bg-surface-disabled p-4 text-xs">
                      {this.state.error.stack}
                    </pre>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;
